import {
	Badge,
	Button,
	Col,
	Component,
	Divider,
	Dropdown,
	Grid,
	Icons,
	Inline,
	Stack,
	Table,
	Text,
} from "@sembark-travel/ui-base"
import { Dialog } from "@sembark-travel/ui-dialog"
import {
	Form,
	SelectField,
	SubmissionError,
	TextInputField,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"
import {
	ListView,
	Search,
	TSearchParams,
	useSearch,
} from "@sembark-travel/ui-list"
import { ButtonLink, useLocationQuery } from "@sembark-travel/ui-router"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { IListResponse, useXHR, XHRInstance } from "@sembark-travel/xhr"
import { useEffect } from "react"
import * as Validator from "yup"
import { PERMISSIONS, useCheckPermissions } from "../Auth"
import { generatePath } from "../router-utils"
import { SelectTripDestination, TTripDestination } from "../TripDestinations"
import { IDocType } from "./store"

function XHR(xhr: XHRInstance) {
	return {
		async get(
			params?: Record<string, unknown>,
			pagination?: "page",
			order?: "desc" | "asc"
		): Promise<IListResponse<IDocType>> {
			return xhr
				.get<IListResponse<IDocType>>("/document-types", {
					params: {
						...params,
						...(pagination ? { pagination } : {}),
						...(order ? { order } : {}),
					},
				})
				.then(({ data }) => data)
		},
		async update(id: number | string, values: IDocType) {
			return xhr
				.patch<{
					data: IDocType
				}>(`/document-types/${id}`, {
					name: values.name,
					destinations: values.destinations.map(
						(destination) => destination.id
					),
				})
				.then(({ data }) => data)
		},
		async destroy(id: number | string) {
			return xhr
				.delete<{ message: string }>(`/document-types/${id}`)
				.then((resp) => resp.data)
		},
	}
}

type TFilters = TSearchParams & {
	destinations?: Array<TTripDestination>
}

function Filters() {
	return (
		<Stack gap="4">
			<SelectField
				name="destinations"
				label="Destinations"
				select={SelectTripDestination}
				multiple
				placeholder="Select destinations..."
				fetchOnMount
			/>
		</Stack>
	)
}

function filtersToRequest(params: TFilters) {
	const { destinations, ...otherParams } = params
	return {
		...otherParams,
		destinations: destinations?.map((u) => u.id),
	}
}

export function DocTypesList() {
	const [query, setQuery] = useLocationQuery()
	const [params, setParams] = useSearch(query)
	useEffect(() => {
		setQuery(params)
	}, [setQuery, params])
	const { hasPermission } = useCheckPermissions()
	const canAddOrUpdate = hasPermission(PERMISSIONS.MANAGE_TRIP_OWNERS)
	return (
		<Search
			initialParams={params}
			onSearch={(params) => setParams({ ...params, page: 1 })}
			title="Document Types"
			Filters={Filters}
			resetParams={{
				q: "",
				page: 1,
			}}
			actions={
				canAddOrUpdate ? (
					<ButtonLink to={generatePath("/document-type/new")}>
						<Inline gap="2" alignItems="center">
							Add Document Type
						</Inline>
					</ButtonLink>
				) : null
			}
		>
			<ListView<IDocType>
				pageKey="doc-types"
				params={params}
				fetch={(xhr, params) => {
					const filterParams = filtersToRequest(params)
					return XHR(xhr).get({ ...filterParams }, "page", "desc")
				}}
				onPageChange={(page) => setParams({ ...params, page })}
			>
				{({ items, refresh, xhr }) => (
					<Table
						headers={[
							"Name",
							<Inline gap="1" alignItems="center">
								Destinations
							</Inline>,
							"",
						]}
						bordered
						hover
						responsive
						alignCols={{ 2: "right" }}
						rows={items.map((item) => [
							<Stack gap="px">
								<Text>{item.name}</Text>
							</Stack>,
							<Text>
								<Inline gap="2" maxWidth="xl" flexWrap="wrap">
									{item.destinations?.length > 0
										? item.destinations.map(({ name }) => (
												<Badge primary>{name}</Badge>
											))
										: "-"}
								</Inline>
							</Text>,
							hasPermission(PERMISSIONS.MANAGE_TRIP_OWNERS) ? (
								<Dropdown alignRight key={item.id}>
									<Dropdown.ToggleButton size="sm" level="tertiary">
										<Icons.DotsVertical />
									</Dropdown.ToggleButton>
									<Dropdown.Menu>
										<>
											<Component initialState={false}>
												{({ state, setState }) => (
													<>
														<Dropdown.MenuItem
															onClick={async () => {
																setState(true)
															}}
														>
															Edit Document Type
														</Dropdown.MenuItem>
														<Dialog
															open={state}
															onClose={() => setState(false)}
															title="Edit Document Type"
															sm
														>
															<Dialog.Body>
																<EditDocType
																	initialValue={item}
																	docTypeId={item.id}
																	onSuccess={() => {
																		setState(false)
																		refresh()
																	}}
																	onCancel={() => setState(false)}
																/>
															</Dialog.Body>
														</Dialog>
													</>
												)}
											</Component>
											<Dropdown.MenuItemDivider marginY="0" />
											<Component initialState={false}>
												{({ state, setState }) => (
													<Dropdown.MenuItem
														color="danger"
														onClick={async () => {
															if (state) return
															if (
																window.confirm(
																	`Are you sure you want to delete the doc type "${item.name}"? This action will not remove any existing data linked to this type.`
																)
															) {
																setState(true)
																try {
																	const resp = await XHR(xhr).destroy(item.id)
																	showSnackbar(
																		resp.message || "Deleted successfully"
																	)
																	refresh()
																} catch (e) {
																	setState(false)
																	const error = e as never as Error
																	window.alert(
																		error.message ||
																			"Something event wrong. Please try after sometime."
																	)
																}
															}
														}}
													>
														{state ? (
															"Please wait..."
														) : (
															<>
																<Icons.Trash /> Delete
															</>
														)}
													</Dropdown.MenuItem>
												)}
											</Component>
										</>
									</Dropdown.Menu>
								</Dropdown>
							) : null,
						])}
					/>
				)}
			</ListView>
		</Search>
	)
}

function EditDocType({
	initialValue,
	docTypeId,
	onSuccess,
	onCancel,
}: {
	docTypeId: number
	initialValue: IDocType
	onSuccess: () => void
	onCancel: () => void
}) {
	const xhr = useXHR()
	const validationSchema = validateFormValues(
		Validator.object().shape({
			name: Validator.string()
				.min(2, "Document name must be greater than zero")
				.max(500, "Document name must be less than 500"),
		})
	)

	return (
		<Form
			validate={validationSchema}
			initialValues={initialValue}
			onSubmit={withServerErrors(async (values) => {
				await XHR(xhr).update(docTypeId, values)
				showSnackbar(`Document Type "${values.name}" Updated Successfully`)
				onSuccess()
			})}
		>
			{({ submitting, handleSubmit }) => (
				<form onSubmit={handleSubmit}>
					<Stack gap="4">
						<Grid gap="4">
							<Col xl={12}>
								<TextInputField
									label="Document Name"
									name={`name`}
									type="string"
									placeholder="e.g. Govt. ID"
									min={1}
									max={500}
								/>
							</Col>
							<Col xl={6}>
								<SelectField
									name="destinations"
									label="Destinations"
									select={SelectTripDestination}
									multiple
									placeholder="Select destinations..."
									fetchOnMount
								/>
							</Col>
						</Grid>
						<Divider sm />
						<SubmissionError />
						<Inline gap="4">
							<Button type="submit" disabled={submitting}>
								{submitting ? "Saving..." : "Save Details"}
							</Button>
							<Button
								level="tertiary"
								disabled={submitting}
								onClick={() => onCancel()}
							>
								Cancel
							</Button>
						</Inline>
					</Stack>
				</form>
			)}
		</Form>
	)
}
